import type { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import type { PlayerPackFormValues } from '@library/types/playerPacks/PlayerPackFormSchema';
import type { PlayerPackProjection } from '@library/types/playerPacks/playerPacks';
import { useCallback } from 'react';

export interface UsePlayerPacksPicksProps {
  setPickedProjections: React.Dispatch<
    React.SetStateAction<Array<PlayerPackProjection>>
  >;
  setIsRowSelected: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  form: UseFormReturn<PlayerPackFormValues>;
  fields: UseFieldArrayReturn<PlayerPackFormValues, 'picks'>;
}

export function usePlayerPacksPicks({
  setPickedProjections,
  setIsRowSelected,
  form,
  fields
}: UsePlayerPacksPicksProps) {
  const handleAddProjection = useCallback(
    async (projection: PlayerPackProjection) => {
      fields.append({ projectionId: projection.projectionId, wagerType: '' });
      await form.trigger();

      setPickedProjections((prev) => {
        return [...prev, projection];
      });

      setIsRowSelected((prev) => ({
        ...prev,
        [projection.projectionId]: !prev[projection.projectionId]
      }));
    },
    [fields, form, setPickedProjections, setIsRowSelected]
  );

  const handleRemoveProjection = useCallback(
    async (projection: PlayerPackProjection) => {
      const picks = form.getValues('picks');
      const indexToRemove = picks.findIndex(
        (p) => p.projectionId === projection.projectionId
      );
      fields.remove(indexToRemove);
      await form.trigger();

      setPickedProjections((prev) => {
        return prev.filter((p) => p.projectionId !== projection.projectionId);
      });

      setIsRowSelected((prev) => ({
        ...prev,
        [projection.projectionId]: !prev[projection.projectionId]
      }));
    },
    [form, fields, setPickedProjections, setIsRowSelected]
  );

  return {
    handleAddProjection,
    handleRemoveProjection
  };
}
